/**
 * Trader Results (#383) — authorized fill-activity and settled-position
 * results by trader.
 *
 * The two datasets are alternative accounting grains and are never combined:
 * fill activity reports execution evidence in the selected range, while
 * settled positions report the frozen #112 per-bucket results filtered by
 * observed settlement time. One dataset renders at a time; the CSV download
 * always reflects exactly the visible trader table (not the #1251 fee split).
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { apiFetch } from '../../stores/authStore';
import { useOrgScope } from '../../stores/orgScope';
import { SummaryStrip, SummaryTile } from '../../components/cards/cardKit';
import { SortableTh } from '../../components/atoms/SortableTh';
import { downloadCsv } from '../../lib/infra/csvExport';
import {
  addWeeks,
  currentWeekRange,
  weekRangeContaining,
  type WeekRange,
} from '../../lib/infra/weekRange';
import {
  buildTraderResultsCsv,
  sortTraderResultsRows,
  traderResultsFilename,
  type FeeRoleBreakdown,
  type FillActivityRow,
  type FillFeeBreakdown,
  type LiquidityRole,
  type SettlementResultsRow,
  type TraderResultsDataset,
  type TraderResultsResponse,
  type TraderResultsSortKey,
  type TraderResultsSettlementVenue,
  type TraderResultsVenue,
  type SortDirection,
  type UsAccountFeeRow,
  type UsAccountFeeSection,
  type UsAccountRoleBreakdown,
} from './traderResultsModel';

type Period = 'current' | 'previous' | 'custom' | 'overall';

const EPOCH_ISO = '1970-01-01T00:00:00.000Z';

export function TraderResults({ adminMode }: { adminMode?: boolean }) {
  const scopeOrgId = useOrgScope((s) => s.selectedOrgId);
  const orgKey = adminMode ? scopeOrgId : 'self';

  const [dataset, setDataset] = useState<TraderResultsDataset>('fills');
  const [period, setPeriod] = useState<Period>('current');
  const [week, setWeek] = useState<WeekRange>(() => currentWeekRange());
  const [customDate, setCustomDate] = useState('');
  const [venue, setVenue] = useState<TraderResultsVenue>('all');
  const [settlementVenue, setSettlementVenue] = useState<TraderResultsSettlementVenue>('all');
  const [selectedTraderIds, setSelectedTraderIds] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<TraderResultsSortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>('asc');
  const [data, setData] = useState<TraderResultsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const generationRef = useRef(0);

  // An organization switch must never show the prior org's rows or keep its
  // trader selection: clear the selection during render, before the next
  // request is even issued.
  const [prevOrgKey, setPrevOrgKey] = useState(orgKey);
  if (prevOrgKey !== orgKey) {
    setPrevOrgKey(orgKey);
    setSelectedTraderIds([]);
  }

  // One string identity per distinct request. Data/error/loading reset happens
  // during render on any change, so a scope, dataset, range, venue, or trader
  // switch can never keep showing the previous scope's rows.
  const selectedKey = selectedTraderIds.join(',');
  const requestKey = adminMode && !scopeOrgId
    ? null
    : [orgKey, dataset, period, week.from, week.to, venue, settlementVenue, selectedKey, attempt].join('|');
  const [prevRequestKey, setPrevRequestKey] = useState<string | null>(null);
  if (prevRequestKey !== requestKey) {
    setPrevRequestKey(requestKey);
    setData(null);
    setError(null);
    setLoading(requestKey != null);
  }

  useEffect(() => {
    if (requestKey == null) {
      // No resolvable scope: invalidate any in-flight request so a late
      // response cannot repopulate the cleared view.
      generationRef.current += 1;
      return;
    }
    // Monotonic request generation: a slow prior response can never overwrite
    // a newer scope, dataset, range, venue, or trader selection.
    const generation = ++generationRef.current;
    const params = new URLSearchParams();
    params.set('dataset', dataset);
    params.set('from', period === 'overall' ? EPOCH_ISO : week.from);
    params.set('to', period === 'overall' ? new Date().toISOString() : week.to);
    params.set('venue', dataset === 'settlements' ? settlementVenue : venue);
    for (const id of selectedTraderIds) params.append('traderId', id);
    if (adminMode && scopeOrgId) params.set('orgId', scopeOrgId);

    apiFetch(`/api/manager/org/results-by-trader?${params.toString()}`)
      .then(async (res) => {
        const body: unknown = await res.json().catch(() => null);
        if (generation !== generationRef.current) return;
        if (!res.ok) {
          const message = (body as { error?: string } | null)?.error;
          setError(message ?? `Request failed (${res.status})`);
          setLoading(false);
          return;
        }
        setData(body as TraderResultsResponse);
        setLoading(false);
      })
      .catch(() => {
        if (generation !== generationRef.current) return;
        setError('Network error');
        setLoading(false);
      });
    // requestKey encodes every request input (scope, dataset, range, venue,
    // selection, retry attempt), so it is the one true dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  const sortedRows = useMemo(() => {
    if (!data) return [];
    return sortTraderResultsRows(data.rows as (FillActivityRow | SettlementResultsRow)[], sortKey, sortDir);
  }, [data, sortKey, sortDir]);

  const handleSort = (key: string) => {
    const typed = key as TraderResultsSortKey;
    if (sortKey === typed) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(typed);
      setSortDir('asc');
    }
  };

  const switchDataset = (next: TraderResultsDataset) => {
    if (next === dataset) return;
    setDataset(next);
    setSortKey(null);
  };

  const selectPeriod = (next: Period) => {
    setPeriod(next);
    if (next === 'current') setWeek(currentWeekRange());
    if (next === 'previous') setWeek(addWeeks(currentWeekRange(), -1));
    if (next === 'custom' && customDate) setWeek(weekRangeContaining(customDate));
  };

  const stepWeek = (delta: number) => {
    setWeek((w) => addWeeks(w, delta));
    if (period === 'current' || period === 'previous') setPeriod('custom');
  };

  const toggleTrader = (id: string) => {
    setSelectedTraderIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  };

  const exportCsv = () => {
    if (!data || sortedRows.length === 0) return;
    downloadCsv(
      buildTraderResultsCsv(data.dataset, sortedRows),
      traderResultsFilename(data.dataset, data.from, data.to, period === 'overall'),
    );
  };

  const atCurrentWeek = week.weekStartDate === currentWeekRange().weekStartDate;
  const noCurrentTraders =
    data != null && data.availableTraders.length === 0 && data.rows.length === 0;
  const zeroActivity =
    data != null &&
    !noCurrentTraders &&
    (data.dataset === 'fills' ? data.totals.fills === 0 : data.totals.settledBuckets === 0);

  const toggleButton = (active: boolean) =>
    `rounded-md border px-3 py-1.5 text-xs transition-colors ${
      active
        ? 'border-primary bg-primary/15 text-foreground'
        : 'border-border text-muted-foreground hover:text-foreground'
    }`;

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <button className={toggleButton(dataset === 'fills')} onClick={() => switchDataset('fills')}>
          Fill activity
        </button>
        <button
          className={toggleButton(dataset === 'settlements')}
          onClick={() => switchDataset('settlements')}
        >
          Settled positions (Poly)
        </button>
        <span className="border-border mx-2 h-5 border-l" />
        <button className={toggleButton(period === 'current')} onClick={() => selectPeriod('current')}>
          Current week
        </button>
        <button className={toggleButton(period === 'previous')} onClick={() => selectPeriod('previous')}>
          Previous week
        </button>
        <button className={toggleButton(period === 'custom')} onClick={() => selectPeriod('custom')}>
          Custom week
        </button>
        <button className={toggleButton(period === 'overall')} onClick={() => selectPeriod('overall')}>
          Overall
        </button>
        {period !== 'overall' && (
          <span className="flex items-center gap-1">
            <button
              aria-label="Step back one week"
              className="border-border text-muted-foreground hover:text-foreground rounded-md border p-1"
              onClick={() => stepWeek(-1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <span className="text-muted-foreground text-xs tabular-nums">
              Week of {week.weekStartDate} (ET)
            </span>
            <button
              aria-label="Step forward one week"
              className="border-border text-muted-foreground hover:text-foreground rounded-md border p-1 disabled:opacity-40"
              disabled={atCurrentWeek}
              onClick={() => stepWeek(1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
        {period === 'custom' && (
          <input
            type="date"
            aria-label="Custom week date"
            className="border-border bg-card text-foreground rounded-md border px-2 py-1 text-xs"
            value={customDate}
            onChange={(e) => {
              setCustomDate(e.target.value);
              if (e.target.value) setWeek(weekRangeContaining(e.target.value));
            }}
          />
        )}
        {period === 'overall' && dataset === 'settlements' && (
          <span className="text-muted-foreground text-xs">
            Current snapshot over all observed settlement dates
          </span>
        )}
        <span className="border-border mx-2 h-5 border-l" />
        {dataset === 'fills' ? (
          <>
            <button className={toggleButton(venue === 'all')} onClick={() => setVenue('all')}>
              All
            </button>
            <button className={toggleButton(venue === 'kalshi')} onClick={() => setVenue('kalshi')}>
              Kalshi
            </button>
            <button
              className={toggleButton(venue === 'polymarket')}
              onClick={() => setVenue('polymarket')}
            >
              Polymarket
            </button>
            <button
              className={toggleButton(venue === 'polymarket_us')}
              onClick={() => setVenue('polymarket_us')}
            >
              Polymarket US
            </button>
          </>
        ) : (
          <>
            <button
              className={toggleButton(settlementVenue === 'all')}
              onClick={() => setSettlementVenue('all')}
            >
              All
            </button>
            <button
              className={toggleButton(settlementVenue === 'polymarket')}
              onClick={() => setSettlementVenue('polymarket')}
            >
              Polymarket
            </button>
            <button
              className={toggleButton(settlementVenue === 'polymarket_us')}
              onClick={() => setSettlementVenue('polymarket_us')}
            >
              Polymarket US
            </button>
          </>
        )}
        {/* One group, so the two wrap together and stay right-aligned (#1111). */}
        <span className="ml-auto flex shrink-0 gap-2">
          <button
            className="border-border text-muted-foreground hover:text-foreground rounded-md border px-3 py-1.5 text-xs"
            onClick={() => setAttempt((n) => n + 1)}
          >
            Retry
          </button>
          <button
            className="border-border text-foreground hover:bg-muted rounded-md border px-3 py-1.5 text-xs disabled:opacity-40"
            disabled={sortedRows.length === 0}
            onClick={exportCsv}
          >
            Download CSV
          </button>
        </span>
      </div>

      {data && data.availableTraders.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-xs">Traders:</span>
          {data.availableTraders.map((trader) => (
            <button
              key={trader.id}
              aria-pressed={selectedTraderIds.includes(trader.id)}
              className={toggleButton(selectedTraderIds.includes(trader.id))}
              onClick={() => toggleTrader(trader.id)}
            >
              {trader.username}
            </button>
          ))}
        </div>
      )}

      <p className="text-muted-foreground text-xs">
        <span>
          {dataset === 'settlements'
            ? 'Current positive-residual snapshot; filtered by observed settled time'
            : 'Executed fill evidence in the selected range; not settled results'}
        </span>
        {'. '}
        <span className="font-medium">Do not add fill and settlement values.</span>
      </p>

      {adminMode && !scopeOrgId ? (
        <p className="text-muted-foreground py-8 text-center text-sm">Select an organization</p>
      ) : loading ? (
        <div className="text-muted-foreground flex items-center justify-center gap-2 py-8 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading trader results…
        </div>
      ) : error ? (
        <p className="text-negative py-8 text-center text-sm">{error}</p>
      ) : !data ? null : noCurrentTraders ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          No current traders in this organization.
        </p>
      ) : (
        <>
          {data.dataset === 'fills' ? (
            <>
              <FillSummary totals={data.totals} usWithheld={data.usWithheldExecutions ?? 0} />
              {data.feeBreakdown && (
                <FeeRoleSection breakdown={data.feeBreakdown} usWithheld={data.usWithheldExecutions ?? 0} />
              )}
              {data.usAccounts && data.usAccounts.accounts.length > 0 && (
                <UsAccountFeesSection section={data.usAccounts} />
              )}
              {data.usAccountsHiddenByTraderFilter && (
                <p className="text-muted-foreground text-xs" data-testid="us-account-fees-filtered">
                  Polymarket US fees by account are hidden while a trader filter is on: an account&apos;s figures
                  cover every trader on it.
                </p>
              )}
            </>
          ) : (
            <SettlementSummary totals={data.totals} rows={data.rows} />
          )}
          {zeroActivity && (
            <p className="text-muted-foreground text-sm">
              {data.dataset === 'fills'
                ? 'No fill activity in this range.'
                : 'No settled positions in this range.'}
            </p>
          )}
          <div className="overflow-x-auto rounded-lg border border-border">
            {data.dataset === 'fills' ? (
              <FillTable
                rows={sortedRows as FillActivityRow[]}
                breakdown={data.feeBreakdown}
                sortKey={sortKey}
                sortDir={sortKey ? sortDir : null}
                onSort={handleSort}
              />
            ) : (
              <SettlementTable
                rows={sortedRows as SettlementResultsRow[]}
                sortKey={sortKey}
                sortDir={sortKey ? sortDir : null}
                onSort={handleSort}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

// #1031: a gross sum is '' when every settled bucket in its scope lacks a value (a Polymarket US
// cost or payout not yet verified). It renders as a dash with its reason, never as $0.0000.
const UNKNOWN_TRADING_TITLE = 'No known value: Polymarket US cost basis not established';
const UNKNOWN_SETTLEMENT_TITLE = 'No known value: Polymarket US settled P&L waits on a verified payout';

function grossUsd(value: string, unknownTitle: string) {
  return value === '' ? <span title={unknownTitle}>—</span> : `$${value}`;
}

function hasUnknownGross(row: Pick<SettlementResultsRow, 'tradingRealizedGrossUsd' | 'residualSettlementPnlGrossUsd'>) {
  return row.tradingRealizedGrossUsd === '' || row.residualSettlementPnlGrossUsd === '';
}

function evidenceNote(count: number, singular: string, plural: string, consequence: string) {
  if (count === 0) return null;
  return (
    <p className="text-xs text-amber-400 light:text-amber-700">
      {count} {count === 1 ? singular : plural} {consequence}
    </p>
  );
}

function FillSummary({
  totals,
  usWithheld,
}: {
  totals: Extract<TraderResultsResponse, { dataset: 'fills' }>['totals'];
  usWithheld: number;
}) {
  return (
    <div className="space-y-2">
      <SummaryStrip>
        <SummaryTile label="Executed volume" value={`$${totals.executedVolumeUsd}`} />
        <SummaryTile label="Contracts" value={totals.contracts} />
        <SummaryTile
          label="Fills"
          value={<span data-testid="summary-fills">{totals.fills}</span>}
        />
        <SummaryTile label="Filled orders" value={totals.filledOrders} />
        <SummaryTile
          label="Average execution price"
          value={totals.averageExecutionPriceUsd === '' ? '—' : `$${totals.averageExecutionPriceUsd}`}
        />
        <SummaryTile
          label="Known actual fees"
          value={`$${totals.knownActualFeesUsd}`}
          tone={totals.unknownFeeFills > 0 ? 'warning' : 'default'}
          title="Sum of fills with known fee evidence only; unknown fees are counted, never zeroed"
        />
        <SummaryTile
          label="Rebates (uncapped est.)"
          value={`$${totals.estimatedRebatesUsd}`}
          title="Uncapped estimate — never netted into a result"
        />
      </SummaryStrip>
      {evidenceNote(
        totals.unknownFeeFills,
        'fill with unknown fees',
        'fills with unknown fees',
        '— excluded from the known fee total, never counted as zero.',
      )}
      {evidenceNote(
        totals.unknownRebateFills,
        'fill with unknown rebate evidence',
        'fills with unknown rebate evidence',
        '— excluded from the rebate estimate.',
      )}
      {evidenceNote(
        usWithheld,
        'Polymarket US execution withheld',
        'Polymarket US executions withheld',
        'for conflicting, unreadable or unpriced evidence — not in these totals, so they are incomplete.',
      )}
    </div>
  );
}

function SettlementSummary({
  totals,
  rows,
}: {
  totals: Extract<TraderResultsResponse, { dataset: 'settlements' }>['totals'];
  rows: readonly SettlementResultsRow[];
}) {
  const net = Number(totals.knownTotalRealizedNetUsd);
  return (
    <div className="space-y-2">
      <SummaryStrip>
        <SummaryTile label="Settled buckets" value={totals.settledBuckets} />
        <SummaryTile
          label="Trading realized gross"
          value={grossUsd(totals.tradingRealizedGrossUsd, UNKNOWN_TRADING_TITLE)}
        />
        <SummaryTile
          label="Residual settlement P&L gross"
          value={grossUsd(totals.residualSettlementPnlGrossUsd, UNKNOWN_SETTLEMENT_TITLE)}
        />
        <SummaryTile label="Known realized fees" value={`$${totals.knownRealizedFeesUsd}`} />
        <SummaryTile
          label="Known total realized net"
          value={`$${totals.knownTotalRealizedNetUsd}`}
          tone={net > 0 ? 'positive' : net < 0 ? 'danger' : 'default'}
          title={`${totals.incompleteEvidenceRows} incomplete-evidence rows are excluded from this total`}
        />
        <SummaryTile label="W / L / T" value={`${totals.wins} / ${totals.losses} / ${totals.ties}`} />
      </SummaryStrip>
      {evidenceNote(
        totals.incompleteEvidenceRows,
        'row with incomplete evidence excluded',
        'rows with incomplete evidence excluded',
        'from Known total realized net — unknown is not known-free.',
      )}
      {(hasUnknownGross(totals) || rows.some(hasUnknownGross)) && (
        <p className="text-xs text-amber-400 light:text-amber-700">
          — = no known value (Polymarket US evidence incomplete); never counted as zero.
        </p>
      )}
    </div>
  );
}

const CELL = 'whitespace-nowrap px-3 py-2 text-right tabular-nums';
const TEXT_CELL = 'whitespace-nowrap px-3 py-2 text-left';
const UNKNOWN_COUNT = 'text-amber-400 light:text-amber-700';

const ROLE_LABELS: readonly (readonly [LiquidityRole, string])[] = [
  ['taker', 'Taker'],
  ['maker', 'Maker'],
  ['unknown', 'Role unknown'],
];

const breakdownKey = (traderId: string | null) => traderId ?? 'historical';

// #1251: the fill activity's fees split by liquidity role. Phase stays one "unknown" column until
// G1 records verified game-state evidence (docs/261019FeeBreakdownContract.md).
function FeeRoleSection({ breakdown, usWithheld }: { breakdown: FillFeeBreakdown; usWithheld: number }) {
  return (
    <section className="space-y-1" aria-labelledby="fee-role-heading">
      <h3 id="fee-role-heading" className="text-sm font-medium">Fees by liquidity role</h3>
      <p className="text-muted-foreground text-xs">
        Phase unknown: G1 does not record when a game actually started, so fees are not split into
        pregame and live.
      </p>
      <div className="max-w-full overflow-x-auto rounded-lg border border-border sm:w-fit">
        <FeeRoleTable roles={breakdown.totals} testId="fee-role-totals" />
      </div>
      {usWithheld > 0 && (
        <p className={`text-xs ${UNKNOWN_COUNT}`} data-testid="fee-role-us-withheld">
          {usWithheld === 1
            ? 'The withheld Polymarket US execution is in no role, so this split is incomplete.'
            : `The ${usWithheld} withheld Polymarket US executions are in no role, so this split is incomplete.`}
        </p>
      )}
      <p className="text-muted-foreground text-xs">
        A Kalshi fill stored without its taker flag counts as maker. Each amount is rounded on its own, so
        a split can differ slightly from the figures above; the difference is rounding only. Open a trader
        below for their split.
      </p>
    </section>
  );
}

function FeeRoleTable({ roles, testId }: { roles: FeeRoleBreakdown; testId: string }) {
  return (
    <table className="w-full text-sm" data-testid={testId}>
      <thead className="border-border border-b">
        <tr>
          <th className={`${TEXT_CELL} font-medium`}>Liquidity role</th>
          <th className={`${CELL} font-medium`}>Fills</th>
          <th className={`${CELL} font-medium`}>Known fees</th>
          <th
            className={`${CELL} font-medium`}
            title="Negative recorded fees (Polymarket US maker rebates), already inside Known fees. Not the rebate estimate."
          >
            Rebates within known fees
          </th>
          <th className={`${CELL} font-medium`}>Unknown fee fills</th>
        </tr>
      </thead>
      <tbody>
        {ROLE_LABELS.map(([role, label]) => {
          const cell = roles[role];
          return (
            <tr key={role} data-role={role} className="border-border border-b last:border-0">
              <td className={TEXT_CELL}>{label}</td>
              <td className={CELL}>{cell.fills}</td>
              <td className={CELL}>${cell.knownFeesUsd}</td>
              <td className={CELL}>${cell.negativeKnownFeesUsd}</td>
              <td className={`${CELL} ${cell.unknownFeeFills > 0 ? UNKNOWN_COUNT : ''}`}>
                {cell.unknownFeeFills}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// #1251: the same recorded Polymarket US executions as the fill activity, by account and for the
// organization. Exact decimal text from the relay, shown as sent; never added to a trader row.
// Owner and key follow the US accounts panel (PolymarketUsOrgAccountsPanel); "disabled" stands in for its badge.
function usAccountName(a: UsAccountFeeRow): string {
  if (a.ownership === 'unlisted') return 'Unlisted account';
  const owner = a.ownership === 'pooled' ? 'Org key' : `Personal · ${a.ownerUsername ?? 'former member'}`;
  const key = !a.hasKey ? 'No key' : `${a.keyRevoked ? 'Revoked key' : 'Key'} ••••${a.accessKeyLast4 ?? ''}`;
  return `${owner} · ${key}${a.enabled === false ? ' · disabled' : ''}`;
}

function withheldNote(count: number): string {
  return count === 1 ? '1 withheld execution, in no role' : `${count} withheld executions, in no role`;
}

function UsAccountFeesSection({ section }: { section: UsAccountFeeSection }) {
  return (
    <section className="space-y-1" aria-labelledby="us-account-fees-heading">
      <h3 id="us-account-fees-heading" className="text-sm font-medium">Polymarket US fees by account</h3>
      <p className="text-muted-foreground text-xs">
        Recorded executions placed through G1 only: trades made on these accounts outside G1 are not recorded
        here, and the institutional connection is not included. The organization total adds the accounts and
        is never added to a trader&apos;s row.
      </p>
      <div className="max-w-full overflow-x-auto rounded-lg border border-border sm:w-fit">
        <table className="w-full text-sm" data-testid="us-account-fees">
          <thead className="border-border border-b">
            <tr>
              <th className={`${TEXT_CELL} font-medium`}>Liquidity role</th>
              <th className={`${CELL} font-medium`}>Executions</th>
              <th className={`${CELL} font-medium`}>Contracts</th>
              <th className={`${CELL} font-medium`}>Executed notional</th>
              <th className={`${CELL} font-medium`} title="Positive recorded commission">Fees paid</th>
              <th
                className={`${CELL} font-medium`}
                title="Negative recorded commission: maker rebates the venue credited per execution. Not the rebate estimate."
              >
                Rebates received
              </th>
              <th className={`${CELL} font-medium`} title="A recorded zero commission: known to be free">$0 fee</th>
              <th className={`${CELL} font-medium`} title="No commission recorded: unknown, never counted as zero">
                No fee recorded
              </th>
            </tr>
          </thead>
          {section.accounts.map((a) => (
            <UsAccountRows
              key={a.accountId}
              name={usAccountName(a)}
              roles={a.roles}
              withheld={a.withheldExecutions}
              testId={`us-account-fees-${a.accountId}`}
            />
          ))}
          <UsAccountRows
            name="Organization total"
            roles={section.organization.roles}
            withheld={section.organization.withheldExecutions}
            testId="us-account-fees-organization"
          />
        </table>
      </div>
      <p className="text-muted-foreground text-xs">
        Amounts are exact, not rounded, so they can differ in the last places from the rounded figures above.
        A NO execution&apos;s notional uses 1 − the YES price, which is provisional.
      </p>
    </section>
  );
}

function UsAccountRows({ name, roles, withheld, testId }: {
  name: string; roles: UsAccountRoleBreakdown; withheld: number; testId: string;
}) {
  return (
    <tbody className="border-border border-b last:border-0" data-testid={testId}>
      <tr>
        <th scope="rowgroup" colSpan={8} className={`${TEXT_CELL} bg-muted/30 font-medium`}>
          {name}
          {withheld > 0 && <span className={`ml-2 text-xs font-normal ${UNKNOWN_COUNT}`}>{withheldNote(withheld)}</span>}
        </th>
      </tr>
      {ROLE_LABELS.map(([role, label]) => {
        const r = roles[role];
        return (
          <tr key={role} data-role={role}>
            <td className={TEXT_CELL}>{label}</td>
            <td className={CELL}>{r.executions}</td>
            <td className={CELL}>{r.contracts}</td>
            <td className={CELL}>${r.executedNotionalUsd}</td>
            <td className={CELL}>${r.positiveCommissionUsd}</td>
            <td className={CELL}>${r.negativeCommissionUsd}</td>
            <td className={CELL}>{r.zeroCommissionExecutions}</td>
            <td className={`${CELL} ${r.unknownCommissionExecutions > 0 ? UNKNOWN_COUNT : ''}`}>
              {r.unknownCommissionExecutions}
            </td>
          </tr>
        );
      })}
    </tbody>
  );
}

interface TableProps<T> {
  rows: T[];
  sortKey: TraderResultsSortKey | null;
  sortDir: SortDirection | null;
  onSort: (key: string) => void;
}

function FillTable({
  rows,
  sortKey,
  sortDir,
  onSort,
  breakdown,
}: TableProps<FillActivityRow> & { breakdown?: FillFeeBreakdown }) {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const rolesByRow = useMemo(
    () => new Map(breakdown?.rows.map((r) => [breakdownKey(r.traderId), r.roles]) ?? []),
    [breakdown],
  );
  const toggle = (key: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
  const th = (key: string, label: string, align?: string) => (
    <SortableTh
      sortKey={key}
      activeSortKey={sortKey}
      activeSortDir={sortDir}
      onSort={onSort}
      className={align}
    >
      {label}
    </SortableTh>
  );
  return (
    <table className="w-full text-sm">
      <thead className="border-border border-b">
        <tr>
          {th('trader', 'Trader')}
          {th('executedVolumeUsd', 'Executed volume', 'text-right')}
          {th('contracts', 'Contracts', 'text-right')}
          {th('fills', 'Fills', 'text-right')}
          {th('filledOrders', 'Filled orders', 'text-right')}
          {th('averageExecutionPriceUsd', 'Average execution price', 'text-right')}
          {th('knownActualFeesUsd', 'Known actual fees', 'text-right')}
          {th('unknownFeeFills', 'Unknown fee fills', 'text-right')}
          {th('estimatedRebatesUsd', 'Rebates (uncapped est.)', 'text-right')}
          {th('unknownRebateFills', 'Unknown rebate fills', 'text-right')}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const key = breakdownKey(row.traderId);
          const roles = rolesByRow.get(key);
          const open = roles !== undefined && expanded.has(key);
          return (
            <Fragment key={key}>
              <tr className="border-border border-b last:border-0">
                <td className={TEXT_CELL}>
                  {roles && (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground mr-1 inline-flex align-middle"
                      aria-expanded={open}
                      aria-controls={`fee-role-panel-${key}`}
                      aria-label={`Fees by liquidity role for ${row.trader}`}
                      onClick={() => toggle(key)}
                    >
                      {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </button>
                  )}
                  {row.trader}
                  {row.attribution === 'historical-unassigned' && (
                    <span className="text-muted-foreground ml-1 text-xs">(not a current trader)</span>
                  )}
                </td>
                <td className={CELL}>${row.executedVolumeUsd}</td>
                <td className={CELL}>{row.contracts}</td>
                <td className={CELL}>{row.fills}</td>
                <td className={CELL}>{row.filledOrders}</td>
                <td className={CELL}>
                  {row.averageExecutionPriceUsd === '' ? '—' : `$${row.averageExecutionPriceUsd}`}
                </td>
                <td className={CELL}>${row.knownActualFeesUsd}</td>
                <td className={`${CELL} ${row.unknownFeeFills > 0 ? 'text-amber-400 light:text-amber-700' : ''}`}>
                  {row.unknownFeeFills}
                </td>
                <td className={CELL}>${row.estimatedRebatesUsd}</td>
                <td className={`${CELL} ${row.unknownRebateFills > 0 ? 'text-amber-400 light:text-amber-700' : ''}`}>
                  {row.unknownRebateFills}
                </td>
              </tr>
              {open && (
                <tr className="border-border border-b last:border-0">
                  <td colSpan={10} className="px-3 py-2">
                    <div id={`fee-role-panel-${key}`} className="max-w-full overflow-x-auto sm:w-fit">
                      <FeeRoleTable roles={roles} testId={`fee-role-${key}`} />
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function SettlementTable({ rows, sortKey, sortDir, onSort }: TableProps<SettlementResultsRow>) {
  const th = (key: string, label: string, align?: string) => (
    <SortableTh
      sortKey={key}
      activeSortKey={sortKey}
      activeSortDir={sortDir}
      onSort={onSort}
      className={align}
    >
      {label}
    </SortableTh>
  );
  return (
    <table className="w-full text-sm">
      <thead className="border-border border-b">
        <tr>
          {th('trader', 'Trader')}
          {th('settledBuckets', 'Settled buckets', 'text-right')}
          {th('tradingRealizedGrossUsd', 'Trading realized gross', 'text-right')}
          {th('residualSettlementPnlGrossUsd', 'Residual settlement P&L', 'text-right')}
          {th('knownRealizedFeesUsd', 'Known realized fees', 'text-right')}
          {th('knownTotalRealizedNetUsd', 'Known total realized net', 'text-right')}
          {th('incompleteEvidenceRows', 'Evidence gaps', 'text-right')}
          {th('wins', 'W', 'text-right')}
          {th('losses', 'L', 'text-right')}
          {th('ties', 'T', 'text-right')}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const net = Number(row.knownTotalRealizedNetUsd);
          return (
            <tr key={row.traderId ?? 'historical'} className="border-border border-b last:border-0">
              <td className={TEXT_CELL}>
                {row.trader}
                {row.attribution === 'historical-unassigned' && (
                  <span className="text-muted-foreground ml-1 text-xs">(not a current trader)</span>
                )}
              </td>
              <td className={CELL}>{row.settledBuckets}</td>
              <td className={CELL}>{grossUsd(row.tradingRealizedGrossUsd, UNKNOWN_TRADING_TITLE)}</td>
              <td className={CELL}>
                {grossUsd(row.residualSettlementPnlGrossUsd, UNKNOWN_SETTLEMENT_TITLE)}
              </td>
              <td className={CELL}>${row.knownRealizedFeesUsd}</td>
              <td className={CELL}>
                <span className={net > 0 ? 'text-positive' : net < 0 ? 'text-negative' : ''}>
                  ${row.knownTotalRealizedNetUsd}
                </span>
              </td>
              <td className={`${CELL} ${row.incompleteEvidenceRows > 0 ? 'text-amber-400 light:text-amber-700' : ''}`}>
                {row.incompleteEvidenceRows}
              </td>
              <td className={CELL}>{row.wins}</td>
              <td className={CELL}>{row.losses}</td>
              <td className={CELL}>{row.ties}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
